/**
 * Palette engine — derives the LIGHT theme and the RED / GREEN / BLUE accent
 * variants from the registered DARK token values.
 *
 * The dark palette is the source of truth (every variable's `dark` value comes
 * straight from `tokens.raw.ts`). Rather than hand-author a parallel value for all
 * ~250 variables, this module derives them by COLOUR SCIENCE, with a small set of
 * explicit overrides for the highest-impact roles:
 *
 *   • Neutrals (greys / near-black surfaces / near-white text) are re-mapped
 *     through an ELEVATION LADDER: dark page → soft off-white, dark cards → white,
 *     nested insets → light grey; near-white text → near-black, mid greys → AA text.
 *   • Translucent WHITE hairlines / washes (the dark theme's lift) invert to
 *     translucent DARK hairlines on light.
 *   • Saturated brand / status hues KEEP their hue; if they're used as text /
 *     icons / borders (a `fill`-role name pattern decides) they're DEEPENED until
 *     they clear WCAG AA (4.5:1) on the light surface — bright neons that pop on
 *     black would otherwise fail on white.
 *   • Gradients, glows, shadows and SVG data-URIs are recoloured token-by-token
 *     with the same rules, preserving their structure.
 *
 * Accents recolour the brand channel "aggressively": every brand-ORANGE colour
 * (the CTA / nav-active / selected / glow orange) is hue-substituted to the chosen
 * accent, in both themes, while semantic status colours (success green, danger
 * red, warning amber, the score-grade scale) stay put so meaning never collides
 * with decoration.
 */

import { GRADE_BANDS, HOME_GRADE_RGB_LIGHT, heroGlowBand, glowStops, rgbHex } from './themeGlows';
import { LOGIN, MARKETING_HUB_RGB, OWN_LIST, REGISTER, SLOT, WEBSITE_HUB_RGB } from './tokens.raw';
import { owsVar } from './themeRegistry';
import { FTUX_LIGHT_OVERRIDES } from './ftux-tokens';

/* ── colour parsing / formatting ─────────────────────────────────────────── */

type Rgba = { r: number; g: number; b: number; a: number };

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
const round = Math.round;

const parseHex = (hex: string): Rgba | null => {
  let h = hex.slice(1);
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  if (h.length === 4)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  if (h.length !== 6 && h.length !== 8) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
};

const parseRgb = (str: string): Rgba | null => {
  const m = str.match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const parts = m[1].split(',').map((p) => p.trim());
  if (parts.length < 3) return null;
  return {
    r: clamp(round(parseFloat(parts[0])), 0, 255),
    g: clamp(round(parseFloat(parts[1])), 0, 255),
    b: clamp(round(parseFloat(parts[2])), 0, 255),
    a: parts[3] !== undefined ? clamp(parseFloat(parts[3]), 0, 1) : 1,
  };
};

const parseColor = (value: string): Rgba | null => {
  const v = value.trim();
  if (v.startsWith('#')) return parseHex(v);
  if (/^rgba?\(/i.test(v)) return parseRgb(v);
  return null;
};

/** Format with the same notation footprint as the source (hex when opaque, rgba
 *  otherwise) — keeps the generated CSS tidy and diff-able. */
const formatColor = ({ r, g, b, a }: Rgba): string => {
  const ri = clamp(round(r), 0, 255);
  const gi = clamp(round(g), 0, 255);
  const bi = clamp(round(b), 0, 255);
  if (a >= 1) {
    const hex = (n: number): string => n.toString(16).padStart(2, '0');
    return `#${hex(ri)}${hex(gi)}${hex(bi)}`;
  }
  return `rgba(${ri}, ${gi}, ${bi}, ${Number(a.toFixed(3))})`;
};

/** A CSS colour (hex, `rgb()` / `rgba()`) as an opaque `#rrggbb` (alpha dropped); '' when it is not one. For the
 *  places that must hand a resolved theme colour to something outside the page's CSS (a sandboxed preview frame). */
export const opaqueHex = (value: string): string => {
  const c = parseColor(value);
  return c ? formatColor({ ...c, a: 1 }) : '';
};

/* ── HSL + contrast ──────────────────────────────────────────────────────── */

type Hsl = { h: number; s: number; l: number; a: number };

const rgbToHsl = ({ r, g, b, a }: Rgba): Hsl => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s, l, a };
};

const hslToRgb = ({ h, s, l, a }: Hsl): Rgba => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255, a };
};

const channelLum = (c: number): number => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const relLuminance = ({ r, g, b }: Rgba): number =>
  0.2126 * channelLum(r) + 0.7152 * channelLum(g) + 0.0722 * channelLum(b);
const contrast = (a: Rgba, b: Rgba): number => {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** Perceptual lightness proxy (0-255) for the neutral ladder. */
const perceptualLum = ({ r, g, b }: Rgba): number => 0.299 * r + 0.587 * g + 0.114 * b;
/** A colour reads as neutral (grey/near-black/near-white). Uses ABSOLUTE chroma
 *  (max-min channel spread) as well as HSL saturation: a near-black like #010003
 *  or #070709 has a tiny absolute spread but a huge *relative* saturation, so the
 *  HSL test alone would wrongly treat it as a vivid colour and skip the ladder. */
const isNeutral = (rgb: Rgba): boolean => {
  const max = Math.max(rgb.r, rgb.g, rgb.b);
  const min = Math.min(rgb.r, rgb.g, rgb.b);
  return max - min < 16 || rgbToHsl(rgb).s < 0.12;
};

/* ── the light surfaces this theme is built on ───────────────────────────── */

/** Page / app background — a soft cool off-white (never harsh pure white). */
const LIGHT_PAGE = { r: 0xf5, g: 0xf6, b: 0xf8, a: 1 };
/** Default text-contrast background = the white card most text sits on. */
const LIGHT_CARD = { r: 0xff, g: 0xff, b: 0xff, a: 1 };

/** Card-white for surfaces raised on a hero glow; translucent so the tint reads through. */
const RAISED_ON_GLOW = 'rgba(255, 255, 255, 0.45)';

/** Known dark SURFACE hexes → their light counterparts. The elevation ladder is
 *  not monotonic in luminance (a dark "outer" container is darker than the cards
 *  it holds, but on light the outer is the tinted page and the cards are white),
 *  so the common surfaces are mapped explicitly; other neutrals use the curve. */
const SURFACE_MAP: Record<string, string> = {
  '#000000': '#f5f6f8',
  '#010003': '#f5f6f8',
  '#040305': '#eef0f3',
  '#07060a': '#f5f6f8',
  '#070709': '#ffffff',
  '#0b0b0e': '#eef0f3',
  '#101012': '#eceef1',
  '#101013': '#eceef1',
  '#101014': '#eceef1',
  '#121017': '#eceef1',
  '#141416': '#ffffff',
  '#141417': '#ffffff',
  '#151518': '#ffffff',
  '#161619': '#ffffff',
  '#17181c': '#ffffff',
  '#18181b': '#ffffff',
  '#1c1c20': '#ffffff',
  '#1e1e20': '#f3f4f6',
  '#1f1f23': '#f7f8fa',
  '#231f20': '#eef0f3',
  '#24242a': '#e8eaee',
  '#27272a': '#eef0f3',
  '#282829': '#e8eaee',
  '#2c3043': '#dfe3ea',
  '#2e2e30': '#e4e6ea',
  '#2e2e32': '#e4e6ea',
  '#3f3f46': '#d4d6dc',
};

/** Map a neutral GREY by perceptual luminance: dark → light surface, light → dark
 *  text. Control points (sourceLum → lightLum), linearly interpolated. The high
 *  end (text) lands near-black; the low end (surfaces) near-white. */
// Monotonic-decreasing: dark near-black SURFACES (low lum) → near-white; bright
// text greys (high lum) → near-black; mid greys stay mid. The early plateau keeps
// stray surfaces light; the descent maps zinc text greys to accessible light text
// (zinc-400 muted ≈ #a1a1aa → ~#5e5e62, not near-black).
const NEUTRAL_POINTS: [number, number][] = [
  [0, 250],
  [24, 250],
  [40, 240],
  [52, 232],
  [60, 170],
  [88, 138],
  [118, 108],
  [140, 96],
  [162, 90],
  [185, 70],
  [205, 56],
  [225, 42],
  [245, 28],
  [255, 24],
];
const mapNeutralLum = (lum: number): number => {
  const pts = NEUTRAL_POINTS;
  if (lum <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i += 1) {
    if (lum <= pts[i][0]) {
      const [x0, y0] = pts[i - 1];
      const [x1, y1] = pts[i];
      return y0 + ((y1 - y0) * (lum - x0)) / (x1 - x0);
    }
  }
  return pts[pts.length - 1][1];
};

/* ── role detection from the variable name ───────────────────────────────── */

/** A "fill" role (background / surface / glow / tint / track) — a saturated fill
 *  may stay bright on light; everything else is treated as text/stroke/icon and
 *  is deepened to clear AA. */
const FILL_RE =
  /(^|-)(bg|background|fill|glow|tint|track|wash|shade|scrim|surface|panel|card|sheen|backdrop|overlay|shadow|pill|chip|disc|orb|ring|circle|gradient|menu|pane|sparkle|wave|aurora|hero|base|inset|tile|thumb|badge|dot|stripe|hatch|solid|symbol)(-|$)/;
const isFillRole = (name: string): boolean => FILL_RE.test(name);

/** A FOREGROUND-ink role (text / glyph / on-accent). A near-black ink with such a
 *  name sits ON a vivid accent fill, so on light it must STAY dark (the surface
 *  ladder would otherwise flip it to near-white → unreadable button text). */
const FG_INK_RE = /(^|-)(text|ink|fg|glyph|label|value|heading|caret|cursor)(-|$)|on-accent|on-orange|symbol-ink/;
const isForegroundInk = (name: string): boolean => FG_INK_RE.test(name);

/** A SURFACE/background role — the element's own fill, which in light mode should
 *  read LIGHT (vs an inset/overlay which stays a faint dark recess). */
const SURFACE_NAME_RE = /(^|-)(bg|background|surface|card|panel|base|hero|sheet|container|menu|backdrop)(-|$)/;
const isSurfaceName = (name: string): boolean => SURFACE_NAME_RE.test(name);

/* ── light derivation of a single colour ─────────────────────────────────── */

/** Lower a colour's lightness until it clears `ratio` against `bg` (a cap keeps it
 *  from going pure-black). Keeps hue + saturation so the brand identity survives. */
const deepenForContrast = (hsl: Hsl, bg: Rgba, ratio: number): Rgba => {
  let l = hsl.l;
  for (let i = 0; i < 60; i += 1) {
    const rgb = hslToRgb({ ...hsl, l });
    if (contrast(rgb, bg) >= ratio || l <= 0.12) return rgb;
    l -= 0.015;
  }
  return hslToRgb({ ...hsl, l: Math.max(l, 0.12) });
};

/** Derive the light value of one parsed colour, given its variable name (for the
 *  fill/text role) and the surface it will sit on (defaults to the white card). */
const deriveLightColor = (rgb: Rgba, name: string, bg: Rgba = LIGHT_CARD): Rgba => {
  const hsl = rgbToHsl(rgb);

  // The single brand orange (#ff791b) is CONSTANT across light + dark — the app has
  // exactly one primary orange, so never deepen, lighten or otherwise shift it.
  if (rgb.a === 1 && Math.abs(rgb.r - 255) <= 6 && Math.abs(rgb.g - 121) <= 12 && Math.abs(rgb.b - 27) <= 14) {
    return { r: 255, g: 121, b: 27, a: 1 };
  }

  // SHADOWS stay dark on light. A box-shadow's black would otherwise be re-mapped to
  // a light surface (an invisible shadow); keep it a softened black so the drop
  // shadow still reads against the light page. (White inset-highlight stops, lum
  // high, fall through to the normal rules.)
  if (/shadow/.test(name) && isNeutral(rgb) && perceptualLum(rgb) < 60) {
    return { r: 0, g: 0, b: 0, a: rgb.a < 1 ? Number(Math.max(rgb.a * 0.5, 0.12).toFixed(3)) : 0.4 };
  }

  // Translucent colours: invert the white "lift" to a dark hairline; keep dark
  // overlays darkening (lighter alpha); keep saturated tints' hue, nudge alpha.
  if (rgb.a < 1) {
    if (isNeutral(rgb)) {
      const lum = perceptualLum(rgb);
      if (lum > 150) {
        // white-on-dark hairline / wash → dark hairline on light
        const a = rgb.a <= 0.14 ? clamp(rgb.a * 1.25, 0.04, 0.14) : rgb.a * 0.5;
        return { r: 17, g: 19, b: 26, a: Number(a.toFixed(3)) };
      }
      // A high-alpha dark SURFACE base (a card/panel's own fill) → flip to a light
      // base (composites light over the page).
      if (rgb.a >= 0.5 && isSurfaceName(name)) {
        return { r: 255, g: 255, b: 255, a: Number(rgb.a.toFixed(3)) };
      }
      // A pure-BLACK darkening overlay (rgba(0,0,0,a)) exists to darken a card over
      // the near-black base; on light the card is light, so the overlay must nearly
      // VANISH (otherwise it greys the white card into mud). Scrims/backdrops keep
      // dimming (a modal still darkens the page behind it).
      const pureBlack = rgb.r < 14 && rgb.g < 14 && rgb.b < 14;
      const isScrim = /(scrim|backdrop)(-|$)/.test(name);
      if (pureBlack && !isScrim) {
        return { r: 15, g: 18, b: 25, a: Number(clamp(rgb.a * 0.16, 0.015, 0.14).toFixed(3)) };
      }
      // Other dark translucent (scrims, grey panel fills, dividers) → a faint dark,
      // softer on light. (Card/panel GREY fills get clean light values via explicit
      // overrides where the rule alone would leave them too grey.)
      const a = rgb.a > 0.5 ? rgb.a * 0.5 : rgb.a * 0.72;
      return { r: 15, g: 18, b: 25, a: Number(a.toFixed(3)) };
    }
    // saturated translucent tint — keep hue, bump faint alphas so it reads on white
    const a = rgb.a < 0.3 ? clamp(rgb.a * 1.25, 0, 0.4) : rgb.a;
    const deepened = hsl.l > 0.6 ? hslToRgb({ ...hsl, l: 0.52 }) : rgb;
    return { ...deepened, a: Number(a.toFixed(3)) };
  }

  // Opaque neutral → elevation ladder.
  if (isNeutral(rgb)) {
    // A near-black FOREGROUND ink (button text, on-accent glyph) sits on a vivid
    // accent fill — keep it dark on light too, instead of flipping it to white.
    if (perceptualLum(rgb) < 56 && isForegroundInk(name) && !isFillRole(name)) {
      return { r: 26, g: 26, b: 29, a: 1 };
    }
    const key = formatColor({ ...rgb, a: 1 });
    const mapped = SURFACE_MAP[key];
    if (mapped) {
      const p = parseColor(mapped);
      if (p) return p;
    }
    const l = mapNeutralLum(perceptualLum(rgb));
    // a hair of cool tint for premium neutrals
    return { r: clamp(l - 1, 0, 255), g: clamp(l, 0, 255), b: clamp(l + 2, 0, 255), a: 1 };
  }

  // Opaque saturated → keep hue; deepen if it's a text/stroke role, else keep as a
  // (possibly slightly toned) fill.
  if (!isFillRole(name)) {
    return deepenForContrast(hsl, bg, 4.5);
  }
  // fill: very light saturated fills can vanish on white — pull them down a touch.
  if (hsl.l > 0.82) return hslToRgb({ ...hsl, l: 0.72 });
  return rgb;
};

/* ── string transform (gradients / glows / shadows / data-URIs) ──────────── */

const COLOR_TOKEN_RE = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)|hsla?\([^)]*\)/g;

/** True if the value carries more than just a single bare colour (a gradient,
 *  shadow, data-URI, layered background…) — those go through the token-by-token
 *  transform; a lone colour goes through `deriveLightColor`. */
const isCompound = (value: string): boolean => {
  const trimmed = value.trim();
  const single = parseColor(trimmed);
  return single === null;
};

const transformColorsIn = (value: string, fn: (rgb: Rgba) => Rgba): string =>
  value.replace(COLOR_TOKEN_RE, (token) => {
    const rgb = parseColor(token);
    return rgb ? formatColor(fn(rgb)) : token;
  });

/* ── public: light value for a variable ──────────────────────────────────── */

/** Hand-tuned light values that beat the rules, by variable name. Keeps the most
 *  visible roles pixel-intentional regardless of the derivation heuristics. */
const LIGHT_OVERRIDES: Record<string, string> = {
  '--ows-colors-page-bg': '#f5f6f8',
  /* Surfaces that sit RAISED on a hero card's tinted glow rather than recessed into
     it: the InsightsPanel's three sub-gauge tiles (Operations, Product) and the
     Acquisition hero's two-CTA block (`Hero.styles.ts` `Actions`). Both used to be
     pinned to the inset grey #f1f3f6, which reads as a dent — and which the same
     commit made invisible, since the light hero glow now fades out to that exact
     value. (Left to the generic ladder they would not land there at all: a pure-black
     overlay derives to a near-vanishing rgba(15, 18, 25, 0.048).)
     Deliberately TRANSLUCENT rather than flat white: at full white the tiles cut a
     hard hole in the glow and the card's tint dies at their edge. At 0.45 they lift
     off the glow while the grade hue still reads clearly through them, so they belong
     to the card rather than sitting on top of it.
     One constant, because the two surfaces must read as the same material. */
  '--ows-colors-insights-tile-bg': RAISED_ON_GLOW,
  /* The page-level tab row's selected pill, shared by both campaign browsers. A fill
     alone cannot carry this on light (any tint quiet enough to sit on a white card is
     far under 3:1 against it), so the pill gets a hairline too and `BrowserTab`
     (CampaignBrowser.styles) adds a weight bump: three cues instead of one, none of
     which depend on a fill/background contrast ratio. The hairline is light-only, so
     the CT specs cannot see it; theme/tabPageActive.test.ts guards it. */
  '--ows-acq-tab-page-active-bg': '#eceef1',
  '--ows-acq-tab-page-active-border': 'rgba(17, 19, 26, 0.12)',
  '--ows-acq-action-inset': RAISED_ON_GLOW,
  '--ows-colors-text': '#1b1b1f',
  '--ows-colors-text-strong': '#101013',
  '--ows-colors-text-muted': '#5a5f6b',
  '--ows-colors-text-faint': '#6d727c',
  '--ows-colors-text-dim': '#646a76',
  '--ows-colors-text-bright': '#101013',
  '--ows-colors-heading': '#15151a',
  '--ows-colors-on-accent': '#ffffff',
  // Cursor/grab glyph ink stays pure black on light too (the supplied cursor is white+black in both).
  '--ows-colors-cursor-ink': '#000000',
  // Publish/primary blue: a light cyan on the white card (matching the dark theme's Lumio-cyan hue)
  // rather than the deepened teal `accent` derives to, so the buttons' DARK ink stays legible.
  '--ows-colors-publish-blue': 'hsl(192, 78%, 62%)',
  '--ows-colors-surface-card': '#ffffff',
  '--ows-colors-surface-inset': '#eef0f3',
  // The trouble card fill: a pale rose on light, so the red stroke and copy still read on it.
  '--ows-colors-danger-surface': '#fdecec',
  // Outline-button solid fill: the light-theme inset surface (matches `surface-inset`), so the
  // secondary button stays opaque and legible on the white card instead of the auto-derived value.
  '--ows-colors-cta-outline-bg': '#eef0f3',
  '--ows-colors-hairline': 'rgba(17, 19, 26, 0.09)',
  '--ows-colors-panel-border': 'rgba(17, 19, 26, 0.1)',
  // The rule between inline text actions: the translucent-white derivation halves its 0.15, leaving it fainter
  // than `hairline`; on light it must stay the stronger of the two, as on dark.
  '--ows-colors-inline-divider': 'rgba(17, 19, 26, 0.18)',
  // Toggle off-state track. The auto-derived white-lift hairline (~0.1 dark alpha)
  // is so faint on a white card that the off switch reads as disabled. Use a solid
  // mid-grey so it clearly reads as an interactive control; the white knob keeps
  // strong contrast against it.
  '--ows-colors-toggle-track-off': '#c4c8d2',
  // Modal/sheet backdrops — the app's PAGE background (--ows-colors-page-bg) at
  // 50%, so the content behind FADES OUT to the page colour (not a dark dim); the
  // modal pops via a large soft drop shadow (see Overlay Sheet). Referencing the
  // var keeps it in lock-step with the page colour.
  '--ows-colors-modal-scrim': 'color-mix(in srgb, var(--ows-colors-page-bg) 50%, transparent)',
  '--ows-affiliate-form-backdrop': 'color-mix(in srgb, var(--ows-colors-page-bg) 50%, transparent)',
  '--ows-players-sheet-backdrop': 'color-mix(in srgb, var(--ows-colors-page-bg) 50%, transparent)',
  // Mobile bottom-bar SCRIM (named "shadow" but it's a fade, not a drop shadow —
  // the shadow rule wrongly darkened it). Content must dissolve into the PAGE
  // colour beneath the floating bar, so fade from page-bg to transparent.
  '--ows-mobile-nav-shadow': 'linear-gradient(0deg, var(--ows-colors-page-bg) 35%, transparent 100%)',
  // Campaign-detail hero status tint (running = green, paused = grey). On light the
  // baked dark-base glows lose the tint; overlay a soft status glow on the white
  // hero (transparent base → the white HeroCard shows through).
  '--ows-running-hero-glow':
    'radial-gradient(130% 140% at 0% 0%, rgba(23, 201, 100, 0.18) 0%, rgba(23, 201, 100, 0) 62%)',
  '--ows-paused-hero-glow':
    'radial-gradient(130% 140% at 0% 0%, rgba(110, 110, 115, 0.16) 0%, rgba(110, 110, 115, 0) 62%)',
  // Collapsed brand-switcher logo tile — dark theme leaves it transparent; on light
  // give it a tile so the brand mark separates from the light top bar. Modelled as a
  // dark wash over the page bg so its strength is a real opacity: bumped 4% -> 6%
  // (a 50% increase) so the collapsed mark reads more clearly.
  '--ows-brand-switcher-trigger-bg': 'color-mix(in srgb, #1f2128 6%, var(--ows-colors-page-bg))',
  // The brand-orange CTA uses dark ink in BOTH themes (the 'solid' fill-rule had
  // flipped this near-black ink to white → white-on-orange).
  '--ows-colors-cta-solid-text': '#1f1206',
  '--ows-colors-hover-wash': 'rgba(17, 19, 26, 0.04)',
  // "Brand Deployment" frosted-glass tint. The dark token tints toward BLACK, so on
  // light it has to flip toward WHITE (a dark frost would smear as a grey smudge on
  // the light page). It is kept nearly TRANSPARENT though: the aurora below now runs
  // its subtractive "ink on paper" model over a WHITE base, so its pastel wash is
  // already gentle and a heavy white veil would erase it. What the layer still earns
  // its keep for on light is the `backdrop-filter` blur in the consumer, which
  // diffuses the shader's ribbon into soft drifting light.
  '--ows-brand-deployment-glass': 'linear-gradient(rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.22))',
  // The aurora canvas: WHITE on light (was pinned near-black). The shader switches to
  // its ink model for this theme (BRAND_DEPLOYMENT_AURORA_INK), so it tints the paper
  // instead of adding light to ink, and no longer needs a dark base. This also fixes
  // the completed state, whose green glow carries no white frost and so used to put
  // near-black text on the near-black base.
  '--ows-brand-deployment-surface': '#ffffff',
  // Lift under the confirmation (CTA) card. Dark separates it by fill; on light the
  // card and the section are both white, so it reads by elevation instead: a tight
  // contact shadow plus a soft ambient one, in the same near-black ink the rest of
  // this palette uses.
  '--ows-brand-deployment-card-lift':
    '0 10px 24px -14px rgba(17, 19, 26, 0.32), 0 2px 6px -3px rgba(17, 19, 26, 0.12)',
  // Deploy-step card hairline. Dark leaves the cards borderless (transparent token); on
  // light they take the SAME border as the section shell that contains them. Pointing at
  // the panel-border variable rather than copying its value keeps the two in lock-step.
  '--ows-brand-deployment-card-edge': 'var(--ows-colors-panel-border)',
  // Frosted-glass top highlight. A white specular sheen is a dark-theme idiom: over white
  // paper it conveys nothing, and any opaque value here reads as a seam cutting off the
  // aurora's first row. Fully transparent, so the aurora runs to the panel edge.
  '--ows-brand-deployment-glass-edge': 'rgba(255, 255, 255, 0)',
  // Deploy-step status footers (dot + label). The dark values are pale, high-key colours
  // built for a near-black card: on the light card they collapse to 1.3-2.7:1 contrast and
  // the label is effectively unreadable. Same colour FAMILY per state, dropped to a shade
  // that carries on white: labels clear 4.5:1, dots clear 3:1 (non-text minimum).
  // completed / failed reuse the light palette's existing semantic green and red (the same
  // ones Stripe status, Pulse and the delta pills already use on light) instead of
  // introducing a second set.
  '--ows-brand-deployment-card-status-completed-dot': '#11a953',
  '--ows-brand-deployment-card-status-completed-text': '#0c7a3c',
  '--ows-brand-deployment-card-status-failed-dot': '#e96c75',
  '--ows-brand-deployment-card-status-failed-text': '#c81e2b',
  // No light cyan exists in the palette yet, so active is derived from its own dark hue.
  '--ows-brand-deployment-card-status-active-dot': '#169fcd',
  '--ows-brand-deployment-card-status-active-text': '#1280a5',
  // pending + skipped are WHITE / grey on dark, so there is no hue to carry over: they map
  // onto the palette's own muted inks. Their dark relationship is preserved (pending reads
  // more prominent than skipped), and both stay subordinate to the coloured states.
  '--ows-brand-deployment-card-status-pending-dot': '#949494',
  '--ows-brand-deployment-card-status-pending-text': '#5a5f6b',
  '--ows-brand-deployment-card-status-skipped-dot': '#949494',
  '--ows-brand-deployment-card-status-skipped-text': '#6d727c',
  // "Deploying {brand}" title shine — on light the text is near-black with a softer
  // mid-grey glint (the dark-theme white glint would be invisible on a light page).
  '--ows-gradients-deployment-title-shine': 'linear-gradient(120deg, #4a4f59 40%, #15151a 50%, #4a4f59 60%)',
  // "Brand Deployment" bounce cards. Dark = frosty black; on light flip to a pale
  // white-frost (a dark glass card would read as a black smear on the light page).
  // Same reasoning as the glass tint above. `blur` is non-colour and is untouched.
  '--ows-brand-deployment-card-black-surface': 'linear-gradient(rgba(255, 255, 255, 0.58), rgba(255, 255, 255, 0.66))',
  '--ows-brand-deployment-card-black-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-layout-chrome-content-bg': '#ffffff',
  '--ows-layout-chrome-content-border': '#e6e8ec',
  // Chrome SURFACES (side menu + Lumio panel). Their dark tokens are translucent
  // dark gradients (an overlay over the near-black backdrop); the generic rule
  // keeps them dark, but as light-theme chrome they must be light panels — clean
  // white rails on the off-white page, separated by the gap + a hairline.
  '--ows-layout-chrome-side-menu-bg': '#ffffff',
  '--ows-layout-chrome-side-menu-sep': 'rgba(17, 19, 26, 0.12)',
  '--ows-layout-chrome-top-bar-mobile-bg': '#ffffff',
  // Support-impersonation banner — keep the info-blue fill and its WHITE ink in
  // BOTH themes. The blue self-derives (a saturated fill survives), but the white
  // ink would otherwise be read as a light surface and flipped to near-black on
  // light (unreadable on blue), so both are pinned.
  '--ows-impersonation-banner-bg': '#1d4ed8',
  '--ows-impersonation-banner-bg-hover': '#1a44c4',
  '--ows-impersonation-banner-text': '#ffffff',
  // Onboarding-locked rail: the per-icon frost flips to a 50% WHITE scrim on the
  // white rail (the generic rule keeps a 'scrim'-named dark translucent dark — a
  // dark frost would read as a smudge on white), and the padlock to a mid dark ink
  // that reads on that light frost.
  '--ows-layout-chrome-lock-scrim': 'rgba(255, 255, 255, 0.5)',
  '--ows-layout-chrome-lock-icon': '#3f3f46',
  '--ows-lumio-bg': '#ffffff',
  '--ows-lumio-border': 'rgba(17, 19, 26, 0.1)',
  // Composer (chat input) typed copy + placeholder. The dark tokens are near-white
  // (#FAFAFA), which the neutral-surface rule reads as a light surface and maps DOWN
  // the elevation ladder to a pale grey — low-contrast ink on the white pill. Pin the
  // typed copy to the standard light body ink and the placeholder to a mid grey that
  // clears AA on that pill. Deliberately states its own value rather than tracking the
  // auth field's: that one is kept-dark now (#18181b) and the two no longer match.
  // Covers desktop + mobile.
  '--ows-lumio-composer-text': '#1b1b1f',
  '--ows-lumio-composer-placeholder': '#6b7280',
  '--ows-lumio-mobile-composer-text': '#1b1b1f',
  '--ows-lumio-mobile-placeholder': '#6b7280',
  // Stop ("generating") orb glyph — the square sits on the vivid magenta→blue
  // gradient disc (stopBg), which the fill-rule keeps saturated on light. The white
  // glyph (#FFFFFF) would otherwise be read as a surface and flipped to near-black,
  // leaving a dark square on the bright disc; pin it white as designed. Desktop +
  // mobile share this token, so the one override covers both.
  '--ows-lumio-stop-icon': '#ffffff',
  // User message bubble copy. The neutral bubble surface derives to a soft grey
  // (#eef0f3) on light; the dark token (#FAFAFA) would wash to a pale grey there, so
  // pin the copy to the standard light body ink for a high-contrast pill.
  '--ows-lumio-user-bubble-text': '#1b1b1f',
  // Suggestion cards (HOT TOPICS / DEEP DIVE). The dark fill is a vivid 0.32-alpha
  // pink→cyan gradient with a neon magenta border — too saturated over the white
  // light-theme panel (it muddies the copy and reads "heavy" against the rest of the
  // white-card theme). On light: drop the fill to a gentle pink→cyan wash on white,
  // soften the border to a translucent magenta hairline, and pin the copy to pure
  // black (the auto-derived near-black #191a1c read faint under the 300-weight font;
  // the card also bumps to weight 400 on light — see CardText). The cyan right-edge
  // glow that doubled the border is now gone in BOTH themes (cardGlow dropped).
  '--ows-lumio-card-bg': 'linear-gradient(154.57deg, rgba(255, 0, 212, 0.15) 19.6%, rgba(0, 224, 255, 0.18) 85.58%)',
  '--ows-lumio-card-border': 'rgba(214, 0, 178, 0.55)',
  '--ows-lumio-card-text': '#000000',
  '--ows-toast-bg': '#1f2126',
  '--ows-toast-text': '#f5f6f8',

  // ── Auth-screen wallpaper (/login + /register) ───────────────────────────
  // The full-bleed auth backdrop is a layered background: a radial vignette over
  // the splash photo. The generic rules already flip the vignette to light (pure
  // black stops → a white centre wash fading to the light page grey), but they
  // cannot swap the PHOTO inside `url(...)`, so light theme was showing a light
  // vignette over the dark 4K shot. Pin the whole layer stack: the LIGHT variant of
  // the photo, under a wash far gentler than the mirrored dark vignette so the shot
  // stays visible instead of being washed out. Registered from `LOGIN.bgImage`,
  // which /register reuses verbatim, so this one override covers both auth screens
  // and nothing else (the configurator wizard keeps its own local
  // `config-bg-4k.jpg` + `--ows-cfg-gradients-page-vignette`).
  '--ows-login-bg-image':
    'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.64) 32%, #f5f6f8 100%), url(https://micros-t.b-cdn.net/config-bg-4k-light-b-1785511305653.webp)',
  // NB: the CARD's own surfaces (bodyBg, cardShadow, titleGradient, the CTA, the field,
  // the footer, the sent-view chips…) are deliberately NOT pinned here one at a time. The
  // whole card is kept-dark in both themes, driven by the KEPT_DARK_LOGIN loop below this
  // object. The wallpaper above is the boundary: everything OUTSIDE the card follows the
  // theme, everything inside it does not.

  // ── Multi-agent palette-audit fixes (role/contrast corrections the generic
  // rules missed): foreground text/dots/labels deepened to clear WCAG AA on
  // light; tokens that must NOT invert (dark toast/gauge dial/logo-preview wash,
  // close-disc glyphs) pinned; a couple of fill/tint
  // regressions corrected. See docs/light-theme.md.
  '--ows-colors-badge-green': '#0b7a3c',
  '--ows-colors-badge-magenta': '#9b27bd',
  '--ows-colors-stripe-success': '#0b7a3c',
  '--ows-colors-stripe-danger': '#c81e2b',
  '--ows-colors-pause-bg': '#c9cace',
  '--ows-acq-spent-dim': 'rgba(17, 19, 26, 0.6)',
  '--ows-acq-badge-sugg-bg': 'rgba(92, 10, 77, 0.12)',
  // NOTE: --ows-home-gauge-label is intentionally NOT pinned here. The gauge
  // card flips to white on light, so the category label (Product/Acquisition/
  // Operations) must read DARK. Its name matches FG_INK_RE ("label"), so the
  // generic derivation deepens it to clear AA — pinning it to #fcfcfc made it
  // white-on-white (invisible). Let it auto-derive like the score number.
  '--ows-home-t600': '#6c6d6f',
  '--ows-pulse-suffix': 'rgba(17, 19, 26, 0.6)',
  '--ows-pulse-t600': '#67686a',
  '--ows-pulse-danger': '#b00000',
  '--ows-pulse-success': '#0c7038',
  '--ows-players-close-glyph': '#ffffff',
  '--ows-lumio-mobile-close-bg-hover': '#ffffff',
  '--ows-lumio-mobile-close-bg': 'rgba(255, 255, 255, 0.94)',
  // The drawer's close disc floats in the gutter ON the drawer scrim, which stays
  // a dark dimming wash on light too (--ows-lumio-drawer-scrim derives to
  // rgba(15,18,25,0.36)). On light, flip the disc to a SOLID WHITE chip with a DARK
  // X so it reads clearly against that dark overlay — the dark-theme's faint
  // white-tint disc + light glyph is near-invisible here. Mirrors the lumio-mobile
  // close control above.
  '--ows-lumio-drawer-close-bg': 'rgba(255, 255, 255, 0.94)',
  '--ows-lumio-drawer-close-bg-hover': '#ffffff',
  '--ows-lumio-drawer-close-glyph': '#1b1b1f',
  '--ows-layout-chrome-side-menu-item': 'rgba(17, 19, 26, 0.6)',
  '--ows-mobile-nav-item': 'rgba(17, 19, 26, 0.6)',
  '--ows-affiliate-form-close-glyph': '#f4f4f5',
  '--ows-casino-tech-step-active-dot': '#11809a',
  '--ows-casino-tech-step-completed-dot': '#1d8660',
  '--ows-casino-tech-step-failed-dot': '#ee0003',
  '--ows-casino-settings-preview-dark': '#1f2024',
  // Drag-over scrim: on light the app behind must fade toward the PAGE colour, not be
  // darkened — same rationale as the modal backdrops above.
  '--ows-casino-settings-drop-scrim': 'color-mix(in srgb, var(--ows-colors-page-bg) 88%, transparent)',
  '--ows-casino-settings-logo-ink-light': '#f4f4f5',
  '--ows-casino-settings-preview-light': '#e9eaee',
  // Country access map inks: the derived light pair lands on forest green / maroon, far too heavy on a white panel;
  // pastel pairs keep the map a quiet preview, blocked still the stronger of the two.
  // The map's ground + country borders: the light darker-tile fill (metric-tile-bg), which is opaque there already.
  '--ows-casino-settings-map-ground': '#f1f3f6',
  '--ows-country-map-ink-allowed-from': '#d6f1e4',
  '--ows-country-map-ink-allowed-to': '#7fcaa6',
  '--ows-country-map-ink-blocked-from': '#ffdcd8',
  '--ows-country-map-ink-blocked-to': '#ec6670',
  // Brand-switcher logo contrast tiles: same rationale as the casino-settings
  // logo previews above — they must NOT invert with the theme, or a light logo
  // would land on a (theme-flipped) light tile and vanish. Kept dark/light.
  '--ows-brand-switcher-logo-bg-dark': '#1f2024',
  '--ows-brand-switcher-logo-bg-light': '#e9eaee',
  '--ows-delta-pill-positive-fg': '#0e7a3c',
  '--ows-delta-pill-negative-fg': '#cc0a00',
  '--ows-card-tones-nearing-budget-badge': '#a87908',
  '--ows-card-tones-exhausted-badge': '#cc1f1a',
  '--ows-channel-card-tone-nearing-budget-accent': '#a87908',
  '--ows-channel-card-tone-exhausted-accent': '#d11600',
  '--ows-toast-icon': '#5fe3b4',
  // Shooting-star / comet streak (home Lumio banner + Pulse value card). The dark
  // streak ends in white (invisible on a white card); on light the wave bands switch
  // to multiply-blend, so the streak needs SATURATED stops (cyan → indigo, no white).
  '--ows-gradients-pulse-streak':
    'linear-gradient(90deg, rgba(8, 145, 178, 0) 0%, rgba(8, 145, 178, 0.55) 68%, rgba(67, 56, 202, 0.95) 100%)',

  // ── Premium light elevation (multi-agent per-page pass) ──────────────────
  // White CARDS (#ffffff) on SUNKEN containers (#ecedf1), recessed INSETS (#f1f3f6);
  // hairlines do the separation. Kills the muddy translucent greys so the studio
  // reads like the Acquisition "Lumio channels" / campaign table reference.
  '--ows-home-panel': '#f1f3f6',
  '--ows-pulse-card-overlay': 'rgba(0, 0, 0, 0)',
  '--ows-pulse-card-base': '#ffffff',
  '--ows-pulse-panel': '#f1f3f6',
  '--ows-acq-card-outer': '#ecedf1',
  '--ows-acq-card-inner': '#ffffff',
  '--ows-acq-camp-card': '#ffffff',
  '--ows-acq-card-base': '#ffffff',
  '--ows-acq-card-overlay': 'rgba(15, 18, 25, 0)',
  '--ows-acq-panel': '#f1f3f6',
  // Locked-card frost (LockVeil). The neutral dark scrim auto-derives to a dark
  // translucent overlay (rgba(15,18,25,0.4)); over a light card that composites to
  // a muddy mid-grey, so a locked channel card reads noticeably darker than its
  // live neighbours. Pin it to a translucent version of the panel grey so a locked
  // card frosts to the SAME calm light-grey as the active cards (home #f1f3f6 cards
  // / acquisition white cards) — the 8px blur + the "Unlocking soon..." caption
  // carry the locked cue, not a heavy grey wash. (ink auto-derives to a deep ink.)
  '--ows-lock-veil-scrim': 'rgba(241, 243, 246, 0.8)',
  '--ows-acq-advisory-bg': '#f1f3f6',
  '--ows-acq-bar-track': '#f1f3f6',
  '--ows-acq-chd-bar-track': '#f1f3f6',
  // Deeper than the #ecedf1 container it sits on, so the segmented tab strip reads as a
  // clear recessed track (the old #e3e5ea was only ~9 levels off → near-invisible on a
  // same-colour container). Shared by the campaign / promotion tables + the wallet tabs.
  '--ows-acq-tab-row': '#d6d9e1',
  '--ows-acq-tab-active-bg': '#ffffff',
  '--ows-acq-badge-muted-bg': '#dfe1e6',
  '--ows-acq-focus-bg': '#f1f3f6',
  '--ows-colors-overlay-soft': 'rgba(255, 255, 255, 0)',
  '--ows-colors-metric-tile-bg': '#f1f3f6',
  '--ows-colors-surface-soft': '#ffffff',
  '--ows-colors-surface-faint': '#f1f3f6',
  '--ows-colors-surface-muted': '#f1f3f6',
  '--ows-colors-cta-pill-bg': '#ffffff',
  '--ows-colors-hero-bg': '#ffffff',
  '--ows-colors-footer-bg': '#ffffff',
  '--ows-colors-plan-badge-bg': '#f1f3f6',
  '--ows-colors-plan-badge-gradient-bg': '#f1f3f6',
  '--ows-colors-plan-cta-selected-bg': '#f1f3f6',
  '--ows-players-container-bg': '#ecedf1',
  '--ows-players-sheet-inset': '#f1f3f6',
  '--ows-casino-tech-tile-bg': '#f1f3f6',
  '--ows-casino-tech-mono-bg': '#f1f3f6',

  // Composite-gradient surfaces (not simple tokens) → clean light cards.
  '--ows-gradients-warm': '#ffffff', // Acquisition insights / gauge-header card
  '--ows-gradients-cool': '#ffffff',
  '--ows-gradients-feature-card': '#ffffff', // KPI tiles, product/feature cards, operations panels
  '--ows-gradients-modal-panel': '#ffffff', // payment modal panel
  '--ows-gradients-plan-selected':
    'radial-gradient(130% 130% at 100% 90%, rgba(255, 121, 27, 0.14), rgba(0, 0, 0, 0) 70%), #ffffff',
  '--ows-campaign-detail-card-bg': '#ffffff',

  // Spotlight / "Important Now" banner: a clean sunken shell with WHITE notice
  // cards carrying a soft tonal glow (was a muddy dark aurora over near-black).
  // The shell is lightened toward the page so the white cards read as raised.
  '--ows-important-now-shell':
    'radial-gradient(circle 560px at 7% -40%, rgba(255, 99, 52, 0.05) 0%, rgba(255, 99, 52, 0) 70%), radial-gradient(circle 520px at 26% -22%, rgba(151, 71, 226, 0.04) 0%, rgba(151, 71, 226, 0) 72%), #f3f3f6',
  // The CARDS render from IMPORTANT_NOW_TONE[tone].bg → the `…-tone-…-bg` vars
  // below (NOT the standalone glow tokens). The generic rule derived them to a
  // heavy cyan/blue tint over a 64%-translucent white, which muddied the card and
  // sank text contrast. Pin each to an OPAQUE white card with only a faint
  // bottom-right tonal glow, so banner copy stays easily readable.
  '--ows-important-now-tone-lumio-bg':
    'radial-gradient(circle 340px at 100% 88%, rgba(0, 168, 220, 0.08) 0%, rgba(90, 120, 235, 0.045) 45%, rgba(0, 168, 220, 0) 74%), #ffffff',
  '--ows-important-now-tone-critical-bg':
    'radial-gradient(circle 320px at 100% 88%, rgba(255, 38, 27, 0.07) 0%, rgba(255, 38, 27, 0.03) 40%, rgba(255, 38, 27, 0) 72%), #ffffff',
  '--ows-important-now-tone-action-bg':
    'radial-gradient(circle 320px at 100% 88%, rgba(255, 121, 27, 0.07) 0%, rgba(255, 121, 27, 0.03) 40%, rgba(255, 121, 27, 0) 72%), #ffffff',
  '--ows-important-now-tone-info-bg': '#ffffff',
  // The premium (feature-upgrade) tone joined the same card shape; same treatment, its own
  // gold-to-violet glow kept faint on white.
  '--ows-important-now-tone-premium-bg':
    'radial-gradient(circle 360px at 100% 88%, rgba(255, 196, 92, 0.09) 0%, rgba(226, 140, 66, 0.05) 38%, rgba(151, 71, 226, 0.03) 70%, rgba(151, 71, 226, 0) 100%), #ffffff',
  // The premium eyebrow is a pale peach on dark; on white it needs the deeper gold the tier pill
  // text already resolves to, or it disappears into the card.
  '--ows-important-now-tone-premium-eyebrow': '#a8651a',
  // The action eyebrow keeps the brand orange, 2.6:1 on white: the deeper orange the palette already gives orange text
  // in light, about 6:1.
  '--ows-important-now-tone-action-eyebrow': '#a8430b',
  // The Spotlight's aurora shell (SPOTLIGHT_SHELL, the ows-ftux prototype's, dark only there). Derived, its glass
  // tinted toward black and smeared the light page; pinned here instead: the sunken container's grey (so the white
  // cards still read as raised), the aurora's pastel ink kept under a lighter white frost (together about the old
  // shell's #f3f3f6, colour gathering at the foot), and a lit glass edge along its top under the hairline.
  '--ows-spotlight-shell-surface': '#ecedf1',
  '--ows-spotlight-shell-glass': 'linear-gradient(rgba(255, 255, 255, 0.26), rgba(255, 255, 255, 0.34))',
  '--ows-spotlight-shell-glass-edge': 'rgba(255, 255, 255, 0.6)',
  // Its header bulb, unlit on light (its glow is dropped there, a light glow cannot read on a light shell): the ring
  // in the header's own ink, the base bars a step lighter (3.4:1 on the shell), as white over grey reads on dark.
  '--ows-important-now-bulb-light': 'var(--ows-important-now-header-accent)',
  '--ows-important-now-bulb-base': '#7d828e',

  // Website and Marketing hub area cards (pages/website, pages/marketing-assets): Home's score gauge card glow on light,
  // in each area's own colour (the colour from the top-left, fading to the light grey; see components/GlowCard).
  ...Object.fromEntries(
    Object.entries(WEBSITE_HUB_RGB).map(([area, rgb]) => [
      owsVar('WEBSITE_HUB_AREA', area, 'glow'),
      glowStops(rgb, 'light'),
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(MARKETING_HUB_RGB).map(([area, rgb]) => [
      owsVar('MARKETING_HUB_AREA', area, 'glow'),
      glowStops(rgb, 'light'),
    ]),
  ),
  '--ows-website-hub-swatch-ring': 'rgba(17, 19, 26, 0.08)',
  // Status chip inks: a `chip` name reads as a fill, so the bright dark-theme inks would stay as they are on the
  // light tint (1.60 to 2.19:1). Deeper inks of the same hue clear AA on their tint over the white card.
  '--ows-website-hub-chip-problem-ink': '#b91c1c',
  '--ows-website-hub-chip-waiting-ink': '#8a5a00',
  '--ows-website-hub-chip-working-ink': '#0f6f86',

  // Casino "Technical details" deployment hero (live / deploying / attention) — was
  // a muddy dark card; clean white with a soft top-left status tint.
  '--ows-casino-tech-glow-live':
    'radial-gradient(circle 300px at 0% 0%, rgba(23, 201, 100, 0.1) 0%, rgba(23, 201, 100, 0) 60%), #ffffff',
  '--ows-casino-tech-glow-deploying':
    'radial-gradient(circle 320px at 0% 0%, rgba(46, 197, 232, 0.1) 0%, rgba(46, 197, 232, 0) 60%), #ffffff',
  '--ows-casino-tech-glow-attention':
    'radial-gradient(circle 300px at 0% 0%, rgba(255, 38, 27, 0.1) 0%, rgba(255, 38, 27, 0) 60%), #ffffff',

  // Campaign-asset wireframe thumbnails (running campaign). The thumb is
  // `linear-gradient($from → ASSET_THUMB_BASE)`; on light the dark `from` fading to
  // a near-black base read muddy. Give a light base + pale tonal `from` tints so
  // each preview is a soft, clean colour wash (the wireframe sits on top).
  '--ows-asset-thumb-base': '#eef0f3',
  '--ows-asset-preview-amber-from': '#ffe7d2',
  '--ows-asset-preview-ember-from': '#ffe0cd',
  '--ows-asset-preview-tangerine-from': '#ffeada',
  '--ows-asset-preview-violet-from': '#f1e3fb',
  '--ows-asset-preview-periwinkle-from': '#e7ebfc',
  '--ows-asset-preview-royal-from': '#e4eafd',

  // ── Readability / contrast tuning (light) ────────────────────────────────
  // "Activate to unlock" / "Upgrade to unlock" eyebrow — meant to recede, but the
  // inverted white-19% was nearly invisible. A muted-but-readable dark wash.
  '--ows-colors-unlock-label': 'rgba(17, 19, 26, 0.42)',
  // You-vs-Market comparison-bar FILLS are fills, not text — keep them vivid (the
  // generic rule had deepened them to muddy brown/teal). Slightly toned from neon.
  '--ows-feature-tone-colors-leveling': '#ef6c00',
  '--ows-feature-tone-colors-inactive': '#ef3328',
  '--ows-feature-tone-colors-maxed': '#13a554',
  '--ows-feature-tone-colors-benchmark': '#0e9cb8',
  // You-vs-Market "market range" track HATCH. The dark grey stripe auto-derives to a
  // ~27%-navy line that washes out on the near-white track (#eef0f3) — the empty
  // market range reads as blank. Re-author the same diagonal SVG with a stronger
  // grey so the range texture stays legible on light. (The hatch is a baked data-URI,
  // so the stripe colour can't be themed indirectly — override the whole var.)
  '--ows-vs-bar-hatch-track':
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='6' height='6'><path d='M0 6L6 0M-1 1L1 -1M5 7L7 5' stroke='rgba(17, 19, 26, 0.4)' stroke-width='1.3' stroke-linecap='square'/></svg>\")",
  // The level chip (L0–L3 / "0") is a DARK badge riding the fill: in dark theme the
  // tone fill is knocked back ~72% (chipShade) / ~24% (chipTint, the no-fill case) and
  // carries light text. On light the generic rule nearly-vanishes those black overlays,
  // so the chip turned the SAME bright colour as the fill with low-contrast text. Keep
  // the badge dark (and its text light) so the level reads on both the fill and the
  // light track — same visual language as dark.
  '--ows-vs-bar-colors-chip-text': '#f4f4f5',
  '--ows-gradients-chip-shade': 'linear-gradient(rgba(15, 18, 25, 0.72), rgba(15, 18, 25, 0.72))',
  '--ows-gradients-chip-tint': 'linear-gradient(rgba(15, 18, 25, 0.5), rgba(15, 18, 25, 0.5))',
  // Plan "/month" period + struck previous price — were too ghosted to read.
  '--ows-colors-text-ghost': 'rgba(17, 19, 26, 0.52)',
  // Eyebrows: "Campaign offering", "STEP 1/2 …" (suggested campaign/promotion).
  '--ows-acq-chd-period': '#b85309',
  // Suggested campaign/promotion art-style selection — soften the warm glow on the
  // selected card so the selection reads premium, not harsh (border softened too).
  '--ows-campaign-detail-card-selected-glow':
    'radial-gradient(60% 70% at 0% 0%, rgba(255, 121, 27, 0.12), rgba(255, 121, 27, 0) 70%), radial-gradient(120% 45% at 50% 100%, rgba(255, 121, 27, 0.1), rgba(255, 121, 27, 0) 72%)',

  /* ── Customizer wizard ("Take your website live") ─────────────────────────
   * The wizard is an immersive near-black flow (its own page vignette, neon
   * deploy cinematic, white-on-dark cards). The generic rule can't flip that to
   * the studio's premium-light surface model on its own, so the structural
   * roles are authored here: white cards on the #f5f6f8 page, hairline borders,
   * dark ink, and light-toned variants of the gradients/scrims/vignettes. The
   * brand orange + the semantic green/blue stay put (they self-derive); only the
   * surfaces, neutrals and composite gradients are pinned. See
   * pages/customizer + docs/light-theme.md. */
  // Surfaces — white cards / inputs on the light page; hairline separation.
  '--ows-cust-page-bg': '#f5f6f8',
  '--ows-cust-field-bg': '#ffffff',
  '--ows-cust-field-border': 'rgba(17, 19, 26, 0.12)',
  '--ows-cust-panel-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-panel-divider': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-surface-card': '#ffffff',
  // Text / icons — `white` is used ONLY as a foreground colour, so it flips to ink.
  '--ows-cust-white': '#1b1b1f',
  '--ows-cust-text-label': '#5a5f6b',
  '--ows-cust-text-placeholder': '#8a8f99',
  '--ows-cust-text-muted': '#5a5f6b',
  '--ows-cust-text-faint': '#6d727c',
  // Orange-as-TEXT (eyebrows, step headings, "In progress", inline links): the
  // engine pins the brand orange #ff791b constant across themes, which is only
  // ~2.6:1 on white. Deepen this text-only role to a burnt orange (~6:1 on the
  // white card) so it stays AA-legible; the `accent` FILL/CTA orange is untouched.
  '--ows-cust-accent-text': '#a8430b',
  // Progress / CTA neutrals.
  '--ows-cust-progress-track': '#e2e4ea',
  '--ows-cust-cta-disabled-bg': 'rgba(17, 19, 26, 0.1)',
  // World map — light-grey land on the light frame.
  '--ows-cust-map-base': 'rgba(17, 19, 26, 0.06)',
  '--ows-cust-map-stroke': 'rgba(17, 19, 26, 0.08)',
  '--ows-cust-map-frame-border': 'rgba(17, 19, 26, 0.1)',
  // Header / dropdown menu.
  '--ows-cust-header-avatar-bg': 'rgba(17, 19, 26, 0.05)',
  '--ows-cust-menu-bg': '#ffffff',
  '--ows-cust-menu-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-row-hover': 'rgba(17, 19, 26, 0.04)',
  '--ows-cust-flag-ring': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-coming-soon': '#6d727c',
  '--ows-cust-soon-pill-bg': 'rgba(17, 19, 26, 0.06)',
  '--ows-cust-row-currency': '#5a5f6b',
  // Softer open-menu shadow — the 0.6-alpha black drop shadow is too heavy on light.
  '--ows-cust-menu-shadow': '0 12px 32px -8px rgba(17, 19, 26, 0.18)',
  // Mobile flow.
  '--ows-cust-mobile-hairline': 'rgba(17, 19, 26, 0.08)',
  '--ows-cust-stage-num-idle': '#e2e4ea',
  // Lock-step with --ows-cust-stage-num-idle (light idle grey) at 0 alpha, so the
  // morph globe plate fades out on its OWN hue instead of cross-fading through a
  // muddy dark mid-tone the auto-derivation would otherwise pick.
  '--ows-cust-stage-num-idle-transparent': 'rgba(226, 228, 234, 0)',
  '--ows-cust-stage-num-idle-text': '#000000', // idle stage ink (2 · 3 · globe): pure black on light (pure white on dark)
  '--ows-cust-intro-sub': '#5a5f6b',
  '--ows-cust-mobile-track': 'rgba(17, 19, 26, 0.12)',
  // Step 2 — suggestion cards.
  '--ows-cust-eyebrow-text': '#2a2d35',
  '--ows-cust-card-surface': '#ffffff',
  '--ows-cust-card-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-count-chip-bg': '#eef0f3',
  '--ows-cust-count-chip-text': '#0f8a44',
  '--ows-cust-free-color': '#0f9d52',
  '--ows-cust-done-chip-bg': 'rgba(23, 201, 100, 0.16)',
  '--ows-cust-done-chip-text': '#0f9d52',
  '--ows-cust-skeleton-bg': 'rgba(17, 19, 26, 0.06)',
  '--ows-cust-skeleton-shine': 'rgba(17, 19, 26, 0.04)',
  // Step 2 — connect-domain sub-flow.
  '--ows-cust-connect-input-bg': '#ffffff',
  '--ows-cust-connect-input-placeholder': '#8a8f99',
  '--ows-cust-connect-btn-disabled-bg': '#d3d6dd',
  '--ows-cust-connect-check': '#0f9d52',
  '--ows-cust-chosen-field-bg': '#eef0f3',
  '--ows-cust-dns-heading': '#15151a',
  '--ows-cust-dns-body': '#3a3e47',
  '--ows-cust-dns-toggle': '#5a5f6b',
  '--ows-cust-dns-table-wrap-bg': '#eef0f3',
  '--ows-cust-dns-table-body-bg': '#ffffff',
  '--ows-cust-dns-header-strong': '#15151a',
  '--ows-cust-dns-header-muted': '#5a5f6b',
  '--ows-cust-dns-cell-strong': '#15151a',
  '--ows-cust-dns-row-separator': 'rgba(17, 19, 26, 0.1)',
  '--ows-cust-dns-copy-icon': '#5a5f6b',
  '--ows-cust-dns-col-label': '#6d727c',
  '--ows-cust-mobile-field-bg': '#ffffff',
  // Step 3 — logo.
  '--ows-cust-logo-image-bg': '#eef0f3',
  '--ows-cust-logo-caption': '#5a5f6b',
  // Light-plate caption ink — pinned (the plate is a luminance-driven backing, not a
  // themed surface), so the description stays near-black on the light logo card in
  // both app themes.
  '--ows-cust-logo-caption-ink': '#18181b',
  '--ows-cust-dropzone-text': '#6d727c',
  '--ows-cust-upload-success': '#15151a',
  // Uploaded-logo preview plates are contrast backings (chosen against the logo's
  // own luminance), so they must NOT flip with the theme — a light mark always sits
  // on a dark plate. Pinned to the SAME values the brand-switcher / Website-logos
  // previews use in light theme (a slightly lifted dark so it isn't pure black on
  // the off-white page). The processing scrim + spinner track DO flip: a light
  // frosted overlay (the wizard page is now off-white) under the dark-ink copy.
  '--ows-cust-logo-frame-dark': '#1f2024',
  '--ows-cust-logo-frame-light': '#e9eaee',
  '--ows-cust-logo-processing-scrim': 'rgba(245, 246, 248, 0.82)',
  '--ows-cust-logo-spinner-track': 'rgba(17, 19, 26, 0.16)',
  // Step 4 — deploy chrome (lightened per design; neon stays for the live moment).
  '--ows-cust-deploy-progress-track': 'rgba(0, 160, 190, 0.14)',
  '--ows-cust-deploy-track-review': 'rgba(120, 110, 200, 0.12)',
  '--ows-cust-deploy-step-text': '#5a5f6b',
  '--ows-cust-deploy-step-finished': '#2a2d35',
  '--ows-cust-deploy-completed-label': '#0f9d52',
  '--ows-cust-deploy-check': '#0f9d52',
  '--ows-cust-summary-strong': '#5a5f6b',
  '--ows-cust-lumio-cyan': '#0094b3', // cyan reads too pale on white — deepen for AA
  '--ows-cust-lumio-tip-bg': '#f1f3f6',
  '--ows-cust-lumio-tip-border': 'rgba(17, 19, 26, 0.12)',
  // Keep the deploying wash DARK in light theme too: the deploy "build" moment is a
  // dark neon cinematic in both themes (its magenta→cyan glow below is pinned, and a
  // plus-lighter dot field only reads over a dark backdrop). A light wash here made
  // the white dots vanish and fought the neon glow.
  '--ows-cust-deploying-blur-bg': 'rgba(0, 0, 0, 0.35)',
  // Step 4 — device-screen phase shadows. Lighten the locked vignette to a soft
  // grey wash; keep the magenta→cyan "deploying" glow (the deploy cinematic).
  '--ows-cust-shadows-pending-vignette-desktop':
    'inset 0 0 400px 40px rgba(150, 156, 168, 0.4), inset 0 0 220px 20px rgba(0, 180, 210, 0), inset 0 0 40px 0 rgba(0, 180, 210, 0)',
  '--ows-cust-shadows-pending-vignette-phone':
    'inset 0 0 400px 40px rgba(150, 156, 168, 0.4), inset 0 0 120px 20px rgba(0, 180, 210, 0), inset 0 0 40px 0 rgba(0, 180, 210, 0)',
  '--ows-cust-shadows-deploying-glow-desktop':
    'inset 0 0 400px 40px rgba(255, 0, 212, 0.55), inset 0 0 220px 20px rgba(0, 224, 255, 0.65), inset 0 0 40px 0 rgba(0, 224, 255, 0.7)',
  '--ows-cust-shadows-deploying-glow-phone':
    'inset 0 0 200px 40px rgba(255, 0, 212, 0.55), inset 0 0 120px 20px rgba(0, 224, 255, 0.65), inset 0 0 40px 0 rgba(0, 224, 255, 0.7)',
  // Composite gradients — can't auto-derive; authored as light surfaces/scrims.
  '--ows-cust-gradients-page-vignette':
    'radial-gradient(120% 120% at 50% 45%, rgba(245, 246, 248, 0) 55%, rgba(213, 217, 225, 0.55) 100%)',
  '--ows-cust-gradients-panel': 'linear-gradient(0deg, #ffffff 0%, #ffffff 100%)',
  '--ows-cust-gradients-suggestion-selected':
    'radial-gradient(130% 130% at 100% 95%, rgba(255, 141, 40, 0.16) 0%, rgba(255, 141, 40, 0) 60%), linear-gradient(#ffffff, #ffffff)',
  '--ows-cust-gradients-lumio-text': 'linear-gradient(-79deg, #0094b3 1.3%, #2f6fd0 100%)',
  '--ows-cust-gradients-heading-gradient': 'linear-gradient(90deg, #7a6fd0 0%, #9c7f88 50%, #b06a3a 100%)',
  '--ows-cust-gradients-mobile-footer-scrim': 'linear-gradient(to top, #f5f6f8 62%, rgba(245, 246, 248, 0))',
  '--ows-cust-gradients-connect-footer': 'linear-gradient(0deg, #f1f3f6 0%, #f1f3f6 100%)',
  '--ows-cust-gradients-deploy-progress-text':
    'linear-gradient(37deg, rgba(200, 0, 168, 0.9) 5%, rgba(0, 148, 179, 0.9) 95%)',
  // Progress-bar sheen rides ON the coloured magenta→cyan fill (not the panel), so a
  // white glint reads in both themes — pin it identical so the auto-derivation never
  // flips the translucent white to a dark hairline (which would smudge the fill).
  '--ows-cust-gradients-deploy-sheen':
    'linear-gradient(100deg, transparent 40%, rgba(255, 255, 255, 0.35) 50%, transparent 60%)',
  '--ows-cust-gradients-deploy-sidebar': 'linear-gradient(0deg, #ffffff 0%, #ffffff 100%)',
  '--ows-cust-gradients-preview-panel': 'linear-gradient(90deg, rgba(236, 238, 242, 0) 0%, #eceef2 100%)',
  '--ows-cust-gradients-locked-scrim-desktop':
    'linear-gradient(270deg, rgba(245, 246, 248, 0.92) 0%, rgba(245, 246, 248, 0.42) 100%)',
  '--ows-cust-gradients-locked-scrim-phone':
    'linear-gradient(11deg, rgba(245, 246, 248, 0.96) 8.98%, rgba(245, 246, 248, 0.9) 38.25%, rgba(245, 246, 248, 0.46) 82.16%)',
  // Dot-field edge-feather mask is alpha-only — keep it identical in light so the
  // auto-derivation never recolours it.
  '--ows-cust-gradients-deploy-grid-mask': 'radial-gradient(120% 120% at 50% 50%, #000 40%, rgba(0, 0, 0, 0) 96%)',

  // Step 3 — AI logo "generating" tile. An intentional dark AI-render viewport
  // (the energy is a monochrome dot field drawn on a canvas inside it): it stays a
  // dark immersive card on the off-white page rather than flipping to a light
  // surface, so the dot field reads. Pin the plate/border dark; the depth-lift
  // shadow gains a little presence against the light page.
  '--ows-cust-gen-tile-border': 'rgba(255, 255, 255, 0.07)',
  '--ows-cust-gen-label-ink': 'rgba(228, 232, 242, 0.5)',
  '--ows-cust-gen-label-halo': '#1a1a1e',
  // Light-plate counterparts (overlay still up over a card already swapped to its
  // light plate). Pinned to track that plate: a near-black ink + the off-white plate
  // colour as the halo (matches --ows-cust-logo-frame-light), in both app themes.
  '--ows-cust-gen-label-ink-light': 'rgba(20, 21, 25, 0.55)',
  '--ows-cust-gen-label-halo-light': '#e9eaee',
  '--ows-cust-gen-card-dark': '#18181c',
  '--ows-cust-gradients-gen-plate': 'linear-gradient(180deg, #1a1a1e 0%, #141418 100%)',
  '--ows-cust-shadows-gen-tile-shadow': '0 12px 30px -6px rgba(17, 19, 26, 0.16)',

  /* ── Configurator-v3 wizard ("build your casino" immersive flow) ──────────
   * The wizard is a full-bleed near-black flow (its own page vignette over a 4K
   * texture, glass chrome, white-on-dark cards, a Lumio AI-scan cinematic). Its
   * tokens (theme/configurator-tokens.ts + the per-step *.tokens.ts) are wrapped
   * through themed() and registered at boot (pages/configurator-v3/registerThemeTokens).
   * The generic rule can't flip an immersive flow to the studio's premium-light
   * surface model on its own, so the structural roles are authored here: white
   * cards on the #f5f6f8 page, hairline borders, dark ink, light vignettes/scrims,
   * and light variants of the glass-chrome gradients. The brand orange + the
   * semantic green/blue/cyan self-derive; intentional dark ART (gem cards, the
   * deep-space promo, the success bloom) stays dark by NOT being var-wrapped.
   * Mirrors the customizer block above. Authored by the multi-agent light pass;
   * cross-surface code fixes (kept-dark-card text, Stripe iframe, mix-blend swaps,
   * the backdrop photo, the light logo) live in the components, see git history. */

  // Core palette
  '--ows-cfg-page-bg': '#f5f6f8',
  '--ows-cfg-card-deep': '#ecedf1',
  '--ows-cfg-surface-raised': '#ffffff',
  '--ows-cfg-card-alt': '#f1f3f6',
  '--ows-cfg-force-dark': '#ffffff',
  '--ows-cfg-base50': '#1b1b1f',
  '--ows-cfg-base100': '#e6e8ec',
  '--ows-cfg-base200': '#15151a',
  '--ows-cfg-base300': '#3a3e47',
  '--ows-cfg-base400': '#5a5f6b',
  '--ows-cfg-base500': '#6d727c',
  '--ows-cfg-foreground': '#15151a',
  '--ows-cfg-text': '#1b1b1f',
  '--ows-cfg-text-muted': '#5a5f6b',
  '--ows-cfg-text-dim': '#3a3e47',
  '--ows-cfg-text-faint': '#6d727c',
  '--ows-cfg-warning': '#ffd814',
  '--ows-cfg-success-alt': '#00d66f',
  '--ows-cfg-success-deep': '#1aad19',

  // Wizard chrome + backdrop + user-menu
  '--ows-cfg-chrome-sidebar-bg': '#ffffff',
  '--ows-cfg-chrome-footer-bg': '#ffffff',
  '--ows-cfg-chrome-card-bg': '#ffffff',
  // Docked header + footer frosted glass — light: a translucent WHITE pane (frosty
  // white over the pale scrolling content), with a soft white sheen/highlight.
  '--ows-cfg-chrome-dock-glass-bg': 'rgba(255, 255, 255, 0.55)',
  '--ows-cfg-chrome-dock-glass-highlight': 'rgba(255, 255, 255, 0.7)',
  '--ows-cfg-chrome-dock-glass-sheen': 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 45%)',
  '--ows-cfg-chrome-sidebar-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cfg-chrome-track-bg': '#c4c8d2',
  '--ows-cfg-chrome-track-shadow': 'inset 0px 1px 1.6px 0px rgba(17, 19, 26, 0.12)',
  '--ows-cfg-chrome-switch-shadow': '0px 1px 2px rgba(17, 19, 26, 0.12)',
  '--ows-cfg-chrome-step-inactive': '#6d727c',
  '--ows-cfg-chrome-chip-bg': '#eef0f3',
  '--ows-cfg-chrome-chip-hover': '#e4e7ee',
  '--ows-cfg-chrome-divider-via': 'rgba(17, 19, 26, 0.1)',
  '--ows-cfg-chrome-button-border': 'rgba(17, 19, 26, 0.14)',
  '--ows-cfg-gradients-page-vignette':
    'radial-gradient(120% 120% at 50% 50%, rgba(17, 19, 26, 0) 0%, rgba(17, 19, 26, 0.05) 100%)',
  '--ows-cfg-scrollbar-thumb': 'rgba(17, 19, 26, 0.18)',
  '--ows-cfg-user-menu-surface': '#ffffff',
  '--ows-cfg-user-menu-fg': '#1b1b1f',
  // Logout label deepened to a burnt orange that clears AA on the pale orange
  // wash (the bare brand orange #ff791b was ~2.3:1); ~6.5:1 on the light button.
  '--ows-cfg-user-menu-logout-text': '#a8430b',

  // Design step
  '--ows-cfg-design-card-base': '#ffffff',
  '--ows-cfg-design-logo-tile': '#f1f3f6',
  '--ows-cfg-design-style-tile': '#f1f3f6',
  '--ows-cfg-design-card-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cfg-design-card-border-hover': 'rgba(17, 19, 26, 0.18)',
  '--ows-cfg-design-subtitle': '#5a5f6b',
  '--ows-cfg-design-icon-stroke': '#1b1b1f',
  '--ows-cfg-design-check-stroke': '#0c7a3c',
  '--ows-cfg-design-use-theme-text': '#0c7a3c',
  '--ows-cfg-design-use-theme-border': '#0c7a3c',
  '--ows-cfg-design-catalog-result-icon-border': 'rgba(17, 19, 26, 0.1)',
  '--ows-cfg-design-gradients-featured-wash':
    'radial-gradient(120% 120% at 100% 100%, rgba(255,121,27,0.20) 0%, rgba(255,121,27,0.12) 32%, rgba(255,121,27,0.05) 55%, rgba(255,121,27,0) 78%)',
  '--ows-cfg-design-gradients-featured-overlay':
    'linear-gradient(110deg, rgba(17, 19, 26, 0.04) 26%, rgba(17, 19, 26, 0) 93%)',
  '--ows-cfg-design-gradients-compact-overlay':
    'linear-gradient(125deg, rgba(17, 19, 26, 0.03) 26%, rgba(17, 19, 26, 0) 93%)',
  '--ows-cfg-design-gradients-result-overlay':
    'linear-gradient(124deg, rgba(17, 19, 26, 0.03) 65%, rgba(17, 19, 26, 0) 93%)',
  '--ows-cfg-design-gradients-result-icon-fill': 'linear-gradient(135deg, #f3f5f8 16%, #eef0f4 84%)',

  // Players step
  // Selected-card footer + "Recommended" pill scrims stay DARK over the option
  // PHOTO (legibility backing for the white footer label/title — see PLAYERS_FOOTER).
  '--ows-cfg-players-footer-overlay':
    'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.72) 100%),linear-gradient(90deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.16) 100%)',
  '--ows-cfg-players-recommended-fill':
    'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.72) 100%),linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) 100%)',
  // Tools-marquee edge scrim (Promo Branding Tools): fades the looping tools into the
  // stage backdrop at the L/R edges. The dark token fades to near-black ink; the generic
  // derivation whitened the opaque edges but left the transparent stops on dark ink, so
  // the interpolation muddied into a grey band. Pin a CLEAN premultiplied fade to the
  // LIGHT stage backdrop (rgba(245,246,248,…) = --ows-cfg-page-bg #f5f6f8): the SAME ink
  // at every stop, only alpha varies, so the tools dissolve seamlessly into the page.
  '--ows-cfg-players-promo-gradients-tools-edge-scrim':
    'linear-gradient(270deg, rgba(245,246,248,0.92) 0%, rgba(245,246,248,0) 19.805%, rgba(245,246,248,0) 80%, rgba(245,246,248,0.92) 100%)',
  '--ows-cfg-players-card-border-soft': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-players-card-border-hover': 'rgba(17, 19, 26, 0.22)',
  '--ows-cfg-players-footer-hairline': 'rgba(255, 255, 255, 0.22)',
  '--ows-cfg-players-unselected-fade': 'linear-gradient(95deg, #ffffff 56.8%, rgba(255,255,255,0) 98%)',
  '--ows-cfg-players-hl-card-bg': '#ffffff',
  '--ows-cfg-players-hl-pill-bg': '#f1f3f6',
  '--ows-cfg-players-hl-desc-text': '#5a5f6b',
  '--ows-cfg-players-hl-desc-shine':
    'linear-gradient(120deg, #5a5f6b 0%, #5a5f6b 40%, #101013 50%, #5a5f6b 60%, #5a5f6b 100%)',
  '--ows-cfg-players-hl-group-title': '#5a5f6b',
  '--ows-cfg-players-hl-check-white': '#ffffff',
  '--ows-cfg-players-hl-searching-final': '#101013',
  '--ows-cfg-players-hl-video-shadow': '0 24px 64px -24px rgba(17, 19, 26, 0.28)',
  '--ows-cfg-players-hl-pill-logo-fallback': '#5a5f6b',

  // Features cards / tabs / confirm / highlights
  '--ows-cfg-feat-card-bg': '#ffffff',
  '--ows-cfg-feat-tab-track': '#f1f3f6',
  '--ows-cfg-feat-tab-border-unfocused': 'rgba(17, 19, 26, 0.16)',
  '--ows-cfg-feat-tab-text-idle': '#5a5f6b',
  '--ows-cfg-feat-tab-text-hover': '#1b1b1f',
  '--ows-cfg-feat-tab-text-disabled': '#8a8f99',
  '--ows-cfg-feat-confirm-backdrop': 'rgba(245, 246, 248, 0.72)',
  '--ows-cfg-feat-confirm-shadow': '0 16px 40px -12px rgba(17, 19, 26, 0.18), 0 2px 6px rgba(17, 19, 26, 0.06)',
  '--ows-cfg-feat-confirm-title-gradient': 'linear-gradient(90deg, #7a6fd0 0%, #9c7f88 50%, #b06a3a 100%)',
  '--ows-cfg-feat-hl-card-bg': '#ffffff',
  '--ows-cfg-feat-hl-pill-bg': '#f1f3f6',
  '--ows-cfg-feat-hl-desc-text': '#5a5f6b',
  '--ows-cfg-feat-hl-desc-shine':
    'linear-gradient(120deg, #5a5f6b 0%, #5a5f6b 40%, #1b1b1f 50%, #5a5f6b 60%, #5a5f6b 100%)',
  '--ows-cfg-feat-hl-stack1': '#e72b02',
  '--ows-cfg-feat-hl-stack2': '#222222',
  '--ows-cfg-feat-hl-stack3': '#1d2b44',

  // Features Lumio AI-scan + benchmark
  '--ows-cfg-feat-chooser-backdrop': 'color-mix(in srgb, var(--ows-colors-page-bg) 55%, transparent)',
  '--ows-cfg-feat-chooser-container-bg': '#ffffff',
  '--ows-cfg-feat-chooser-container-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-container-shadow': '0 40px 100px -24px rgba(17, 19, 26, 0.28)',
  '--ows-cfg-feat-chooser-header-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-scrim-header': 'linear-gradient(to bottom, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
  '--ows-cfg-feat-chooser-scrim-footer': 'linear-gradient(to top, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
  '--ows-cfg-feat-chooser-lumio-star-stroke': 'rgba(17, 19, 26, 0.18)',
  '--ows-cfg-feat-chooser-lumio-star-fill': 'rgba(17, 19, 26, 0.04)',
  '--ows-cfg-feat-chooser-card-bg': '#f1f3f6',
  '--ows-cfg-feat-chooser-card-border': 'rgba(17, 19, 26, 0.08)',
  '--ows-cfg-feat-chooser-card-shimmer': 'linear-gradient(90deg, transparent, rgba(17, 19, 26, 0.05), transparent)',
  '--ows-cfg-feat-chooser-packs-title': '#101013',
  '--ows-cfg-feat-chooser-status-label': '#5a5f6b',
  '--ows-cfg-feat-chooser-scan-track': '#f1f3f6',
  '--ows-cfg-feat-chooser-scan-track-border': 'rgba(17, 19, 26, 0.06)',
  '--ows-cfg-feat-chooser-competitor-title': '#5a5f6b',
  '--ows-cfg-feat-chooser-pill-bg': '#ffffff',
  '--ows-cfg-feat-chooser-pill-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-pill-name': '#101013',
  '--ows-cfg-feat-chooser-pill-logo-bg': '#f1f3f6',
  '--ows-cfg-feat-chooser-pill-loader': '#5a5f6b',
  '--ows-cfg-feat-chooser-more-counter': '#3a3e47',
  '--ows-cfg-feat-chooser-pack-bg': '#ffffff',
  '--ows-cfg-feat-chooser-pack-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-pack-name': '#101013',
  '--ows-cfg-feat-chooser-pack-desc': '#5a5f6b',
  '--ows-cfg-feat-chooser-best-tag-bg': '#f1f3f6',
  '--ows-cfg-feat-chooser-best-tag-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-old-price': '#6d727c',
  '--ows-cfg-feat-chooser-feature-pill-bg': '#ffffff',
  '--ows-cfg-feat-chooser-feature-pill-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-fp-title': '#101013',
  '--ows-cfg-feat-chooser-footer-bg': '#ffffff',
  '--ows-cfg-feat-chooser-footer-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-chooser-footer-divider': 'rgba(17, 19, 26, 0.18)',
  '--ows-cfg-feat-chooser-footer-price': '#101013',
  '--ows-cfg-feat-chooser-fp-track-bg': '#f1f3f6',
  /* The You-vs-Market bar's INK is kept dark. Its fill (CHOOSER.fpYouFill, a saturated blue
   * gradient) is theme-invariant — the derivation leaves it alone — so the hatch that overlays it
   * and the tier pill riding its tip sit on the SAME backdrop in both themes and must keep the
   * same colours: derived, the white hatch turned into near-black lines on dark blue. Guarded by
   * theme/marketBarInk.test.ts. The hatch BELOW keeps a light value of its own: it only shows in
   * the You->Market gap, where the backdrop is the near-white light track, not the fill. */
  '--ows-cfg-feat-chooser-fp-market-hash':
    'repeating-linear-gradient(-45deg, rgba(17, 19, 26, 0.30) 0px, rgba(17, 19, 26, 0.30) 2px, transparent 2px, transparent 6px)',
  '--ows-cfg-feat-chooser-fp-market-hash-over':
    'repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.5) 0px, rgba(255, 255, 255, 0.5) 2px, transparent 2px, transparent 6px)',
  '--ows-cfg-feat-chooser-fp-tier-bg': '#121214',
  '--ows-cfg-feat-chooser-fp-tier-accent': '#ffffff',
  '--ows-cfg-feat-benchmark-panel-bg': '#ffffff',
  '--ows-cfg-feat-benchmark-panel-overlay': 'rgba(17, 19, 26, 0)',
  '--ows-cfg-feat-benchmark-panel-glow':
    'radial-gradient(120px 90px at 22% 0%, rgba(125, 113, 233, 0.10) 0%, rgba(94, 85, 175, 0.05) 45%, rgba(0, 0, 0, 0) 75%)',
  '--ows-cfg-feat-benchmark-divider': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-feat-benchmark-eyebrow': '#5a5f6b',
  '--ows-cfg-feat-benchmark-caption': '#0094b3',
  '--ows-cfg-feat-benchmark-track': '#f1f3f6',
  // Same theme-invariant ink rule as the detailed bar above (the cyan BENCHMARK.youFill does not
  // derive either) — but this hatch is near-BLACK in both themes, not white: it overlays the light
  // cyan→blue fill, where white stripes wash out. Kept in step with BENCHMARK.marketHashOver.
  '--ows-cfg-feat-benchmark-market-hash-over':
    'repeating-linear-gradient(-45deg, rgba(17, 19, 26, 0.30) 0px, rgba(17, 19, 26, 0.30) 2px, transparent 2px, transparent 6px)',
  '--ows-cfg-feat-benchmark-tier-bg': '#121214',
  '--ows-cfg-feat-benchmark-tier-accent': '#ffffff',
  '--ows-cfg-feat-benchmark-blue-stripes':
    'repeating-linear-gradient(135deg, rgba(17, 19, 26, 0.22) 0 2px, rgba(17, 19, 26, 0) 2px 7px)',
  '--ows-cfg-feat-benchmark-marker': '#5a5f6b',
  '--ows-cfg-feat-benchmark-mini-label': '#5a5f6b',
  '--ows-cfg-feat-benchmark-mini-pill-bg': '#f1f3f6',
  '--ows-cfg-feat-benchmark-mini-logo-border': 'rgba(17, 19, 26, 0.12)',
  '--ows-cfg-feat-benchmark-mini-count': '#101013',

  /* The STUDIO's own You-vs-Market bar (VS_BAR_PACK), read by the product feature cards and the
   * product-detail benchmark bar. Same rule as the two configurator surfaces above, for the same
   * reason: its fill (`youFill`) is a saturated blue gradient the derivation leaves alone, so the
   * hatch that overlays it and the tier pill riding its tip sit on an unchanged backdrop and must
   * keep their dark-theme ink. Derived, the white hatch turned near-black on dark blue and the
   * pill inverted to a white chip with dark ink, splitting one indicator into two designs across
   * the studio and the wizard. The hatch BEHIND the fill (`market-hash`) is deliberately left to
   * derive: it shows in the You->Market gap, over the track, which IS theme-dependent. Guarded by
   * theme/marketBarInk.test.ts. */
  '--ows-vs-bar-pack-market-hash-over':
    'repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.5) 0px, rgba(255, 255, 255, 0.5) 2px, transparent 2px, transparent 6px)',
  '--ows-vs-bar-pack-tier-bg': '#121214',
  '--ows-vs-bar-pack-tier-accent': '#ffffff',

  // Finish review / checkout / payment
  // Success screen follows the light theme (a clean light "Payment successful"
  // rather than a dark island — the shared CFG inks flip to dark ink correctly
  // on the light bg). The pastel heading muds on white, so it deepens; copy /
  // close-hint take readable light inks; the card goes white. The baked dark
  // mobile bloom photo is dropped in light (PaymentSuccess Bloom).
  '--ows-cfg-finish-success-bg': '#f5f6f8',
  '--ows-cfg-finish-success-card-bg': '#ffffff',
  '--ows-cfg-finish-success-copy-muted': '#5a5f6b',
  '--ows-cfg-finish-success-close-hint': '#5a5f6b',
  // Deepened so all three stops clear AA on the light page (the pastel mauve/tan
  // washed out): deep violet → plum → burnt orange, all ~4.8:1+ on #f5f6f8.
  '--ows-cfg-finish-gradients-success-heading': 'linear-gradient(90deg, #5b4bd6 0%, #7a4f6a 50%, #a8531e 100%)',
  '--ows-cfg-finish-pane-bg': '#ffffff',
  '--ows-cfg-finish-terms-text': 'rgba(17, 19, 26, 0.6)',
  '--ows-cfg-finish-input-border-focus': 'rgba(17, 19, 26, 0.45)',
  '--ows-cfg-finish-input-text': '#1b1b1f',
  '--ows-cfg-finish-error-text': '#c81e2b',
  '--ows-cfg-finish-error-icon-bg': 'rgba(200, 30, 43, 0.1)',
  '--ows-cfg-finish-error-icon-border': 'rgba(200, 30, 43, 0.28)',
  '--ows-cfg-finish-error-alert-bg': 'rgba(200, 30, 43, 0.07)',
  '--ows-cfg-finish-error-alert-border': 'rgba(200, 30, 43, 0.26)',
  '--ows-cfg-finish-method-row-bg': '#ffffff',
  // Mexican checkout: a field inside an expanded payment method. On dark it sinks BELOW the
  // raised option card; on light the card is already white, so the relationship inverts to a
  // faint grey inset (a dark fill there would read as a hole, not a field). Kept in lockstep
  // with `MX_FIELD_BG.light`, which hands the same value to Stripe's cross-origin iframe.
  '--ows-cfg-spei-field-bg': '#f4f5f8',
  '--ows-cfg-finish-express-card-bg': '#f1f3f6',
  '--ows-cfg-finish-preview-popup-shadow': '0 30px 80px -24px rgba(17, 19, 26, 0.28)',
  '--ows-cfg-finish-gradients-preview-backdrop':
    'linear-gradient(90deg, rgba(245,246,248,0.55) 0%, rgba(245,246,248,0.55) 100%), linear-gradient(90deg, rgba(236,238,242,0.62) 0%, rgba(236,238,242,0.62) 100%)',

  // Country picker
  '--ows-cfg-country-backdrop':
    'linear-gradient(90deg, rgba(245, 246, 248, 0.62) 0%, rgba(245, 246, 248, 0.62) 100%), linear-gradient(90deg, rgba(236, 237, 241, 0.55) 0%, rgba(236, 237, 241, 0.55) 100%)',
  '--ows-cfg-country-panel-bg': '#ffffff',
  '--ows-cfg-country-hero-bg': 'linear-gradient(0deg, #f1f3f6 0%, #f1f3f6 100%)',

  // Mobile wizard
  '--ows-cfg-mobile-screen-bg': '#f5f6f8',
  '--ows-cfg-mobile-preview-bg': '#f5f6f8',
  '--ows-cfg-mobile-sheet-bg': '#ffffff',
  '--ows-cfg-mobile-panel-bg': '#ffffff',
  '--ows-cfg-mobile-pill-bg': '#f1f3f6',
  '--ows-cfg-mobile-gauge-track': '#ecedf1',
  '--ows-cfg-mobile-detail-backdrop': '#f5f6f8',
  '--ows-cfg-mobile-black-surface': '#ffffff',
  '--ows-cfg-mobile-knockout': '#1f1206',
  '--ows-cfg-mobile-seg-done': '#3a3e47',
  '--ows-cfg-mobile-seg-idle': 'rgba(17, 19, 26, 0.14)',
  '--ows-cfg-mobile-hairline': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-mobile-hairline-soft': 'rgba(17, 19, 26, 0.06)',
  '--ows-cfg-mobile-white': '#1b1b1f',
  '--ows-cfg-mobile-white-faint': 'rgba(17, 19, 26, 0.52)',
  '--ows-cfg-mobile-white-dim': '#5a5f6b',
  '--ows-cfg-mobile-tab-idle': 'rgba(17, 19, 26, 0.45)',
  '--ows-cfg-mobile-chip-bg': '#ecedf1',
  '--ows-cfg-mobile-chip-border': 'rgba(17, 19, 26, 0.07)',
  '--ows-cfg-mobile-you-border': 'rgba(17, 19, 26, 0.14)',
  '--ows-cfg-mobile-tier-muted': '#5a5f6b',
  '--ows-cfg-mobile-tile-bg': '#f1f3f6',
  '--ows-cfg-mobile-chevron-bg': 'rgba(17, 19, 26, 0.06)',
  '--ows-cfg-mobile-style-card-bg': '#ffffff',
  '--ows-cfg-mobile-option-card-bg': '#ffffff',
  '--ows-cfg-mobile-header-fade': 'linear-gradient(to bottom, #f5f6f8 55%, rgba(245, 246, 248, 0) 100%)',
  '--ows-cfg-mobile-header-scrim': 'linear-gradient(to bottom, #f5f6f8 20.192%, rgba(245, 246, 248, 0) 100%)',
  '--ows-cfg-mobile-bottom-fade': 'linear-gradient(to top, #f5f6f8 55%, rgba(245, 246, 248, 0) 100%)',
  '--ows-cfg-mobile-bottom-fade-heavy': 'linear-gradient(to top, #f5f6f8 75%, rgba(245, 246, 248, 0) 100%)',
  '--ows-cfg-mobile-design-scrim':
    'linear-gradient(to top, rgba(245,246,248,0.94) 0%, rgba(245,246,248,0.94) 30%, rgba(245,246,248,0) 100%)',
  '--ows-cfg-mobile-payment-scrim':
    'linear-gradient(90deg, color-mix(in srgb, var(--ows-colors-page-bg, #f5f6f8) 50%, transparent) 0%, color-mix(in srgb, var(--ows-colors-page-bg, #f5f6f8) 62%, transparent) 100%)',
  '--ows-cfg-mobile-tier-pill-bg':
    'radial-gradient(48px 32px at 50% 250%, rgba(17,19,26,0.10), rgba(17,19,26,0.02)), #ffffff',
  '--ows-cfg-mobile-feature-pill-bg':
    'radial-gradient(48px 32px at 50% 250%, rgba(17,19,26,0.10), rgba(17,19,26,0.02)), #ffffff',
  '--ows-cfg-mobile-soft-shadow': '0 4px 8px rgba(17, 19, 26, 0.12)',
  '--ows-cfg-mobile-cluster-shadow': 'drop-shadow(0 4px 8px rgba(17, 19, 26, 0.12))',
  '--ows-cfg-mobile-you-shadow': '0 4px 16px rgba(17, 19, 26, 0.12)',
  '--ows-cfg-mobile-panel-shadow': '0 -4px 12px rgba(17, 19, 26, 0.10)',
  '--ows-cfg-mobile-selector-shadow': 'drop-shadow(0 -4px 8px rgba(17, 19, 26, 0.10))',
  '--ows-cfg-mobile-title-gradient': 'linear-gradient(90deg, #6a5fd0 0%, #8a6f9e 50%, #b46a2e 100%)',
  '--ows-cfg-mobile-detail-gradient': 'linear-gradient(90deg, #6a5fd0 0%, #7d6aa8 100%)',
  '--ows-cfg-mobile-scan-loader-stroke': 'rgba(17, 19, 26, 0.45)',
  '--ows-cfg-mobile-competitor-accent': '#f3dcdc',

  // ── Verify-pass fixes (adversarial light audit) ───────────────────────────
  // Brand orange as TEXT fails AA on white (~2.6:1). Brand stays #ff791b as a
  // FILL, but its text/eyebrow/link uses deepen to a burnt orange; the status
  // green/red used as text also deepen to AA (all text-only in this wizard).
  '--ows-cfg-brand-text': '#a8430b',
  '--ows-cfg-brand-alt': '#a8430b',
  '--ows-cfg-success': '#0c7a3c',
  '--ows-cfg-danger': '#c81e2b',
  // Features HighlightCard copy was missing the players-copy's light inks → it
  // rendered white-on-white on the now-white card. (HL.checkGreen = CFG.success,
  // fixed by the global --ows-cfg-success above.)
  '--ows-cfg-feat-hl-searching-final': '#101013',
  '--ows-cfg-feat-hl-group-title': '#5a5f6b',
  // SwitchToCustomModal → a clean light dialog (white surface, dark cancel label).
  '--ows-cfg-feat-confirm-surface': '#ffffff',
  '--ows-cfg-feat-confirm-cancel-text': '#1b1b1f',
  // Lumio AI-scan neon, deepened so the wordmark / status / scan bar / dot read on
  // the white modal (the dark-theme cyan/white stops washed out on white).
  '--ows-cfg-feat-chooser-lumio-text': 'linear-gradient(90deg, #0094b3 0%, #2f6fd0 50%, #c8009e 100%)',
  '--ows-cfg-feat-chooser-status-text': 'linear-gradient(90deg, #c8009e 0%, #007a8c 50%, #c8009e 100%)',
  '--ows-cfg-feat-chooser-scan-fill':
    'linear-gradient(90deg, #c8009e 0%, #00a0c4 40%, #7a6fd0 50%, #00a0c4 60%, #c8009e 100%)',
  '--ows-cfg-feat-chooser-status-dot': 'linear-gradient(90deg, #c8009e 0%, #007a8c 100%)',
  // Country picker hairlines / hovers (only backdrop/panel/hero were authored).
  '--ows-cfg-country-panel-border': 'rgba(17, 19, 26, 0.10)',
  '--ows-cfg-country-hairline': 'rgba(17, 19, 26, 0.08)',
  '--ows-cfg-country-close-bg': 'rgba(17, 19, 26, 0.06)',
  '--ows-cfg-country-close-hover': 'rgba(17, 19, 26, 0.12)',
  '--ows-cfg-country-row-hover': 'rgba(17, 19, 26, 0.04)',
  // Mobile AI-scan title gradient deepened for AA on the light screen.
  '--ows-cfg-mobile-scan-gradient': 'linear-gradient(22.4deg, #c8009e 5.215%, #007a8c 94.785%)',
  // Device-preview letterbox screens stay dark bezels in light (kept-dark intent —
  // a real device screen reads black; they only show before a nav colour is picked).
  '--ows-cfg-chrome-phone-screen': '#000000',
  '--ows-cfg-chrome-preview-screen': '#0c0c10',
};

// Tuned light values for the score-grade glows (built per band so the hue still
// reads over a near-white card). Keyed to the variables tokens.ts registers.
for (const band of GRADE_BANDS) {
  LIGHT_OVERRIDES[`--ows-hero-glow-${band}`] = heroGlowBand(band, 'light');
  // Home's score cards (the only gauge glow) in their grade colour, as every score ring on light: one source for all of
  // them, so Home's cards and the pages they open stay in sync (the user, 2026-10-21).
  LIGHT_OVERRIDES[`--ows-gauge-glow-${band}`] = glowStops(HOME_GRADE_RGB_LIGHT[band], 'light');
  LIGHT_OVERRIDES[owsVar('GAUGE_GRADE_COLORS', band)] = rgbHex(HOME_GRADE_RGB_LIGHT[band]);
}

// The Lumio "777" slot machine is a kept-dark casino cabinet: pin every SLOT var
// to its dark literal so light theme renders it identically (its white reel glass
// would otherwise be read as a near-white surface and inverted to near-black).
for (const key of Object.keys(SLOT)) {
  LIGHT_OVERRIDES[owsVar('SLOT', key)] = SLOT[key as keyof typeof SLOT];
}

// FTUX - Getting Started pins every --ows-ftux-* var in its own token file (its kept-dark hello included).
Object.assign(LIGHT_OVERRIDES, FTUX_LIGHT_OVERRIDES);

/**
 * The auth CARD on /login + /register is kept-dark, the same move the SLOT cabinet above
 * makes: the light page keeps its pale wallpaper, its dark-on-light logo and its light
 * language picker, but the card itself renders in the dark palette, so the component is
 * one design in both themes instead of two that have to be kept in sync by hand.
 *
 * Listed key by key rather than pinning the whole LOGIN group, because the group also
 * owns things OUTSIDE the card that must keep following the theme:
 *   - `pageBg` / `bgImage` / `scroll*`: the page the card sits on.
 *   - `lang*`: the language picker BELOW the card. This is why the sent-view's back
 *     button and email chip stopped borrowing `langTrigger*` and got their own
 *     `cardChip*` pair: one variable cannot resolve light for the picker and dark for
 *     the card.
 * Everything here reads its value straight off `raw.LOGIN`, so retuning the dark palette
 * retunes light with it and the two can never drift into different colours.
 *
 * `inputFocusBorder` is in the list even though dark ships no focused variant of its own:
 * the focus state is drawn on the card, in both themes, so it is a card colour like the
 * rest and must not derive. It briefly had its own LIGHT_OVERRIDES pin instead, which is
 * how it ended up near-black against a near-black card once the card stopped being white.
 */
const KEPT_DARK_LOGIN: (keyof typeof LOGIN)[] = [
  'bodyBg',
  'cardShadow',
  'titleGradient',
  'subtitle',
  'address',
  'errorBoxBg',
  'errorBoxBorder',
  'errorBoxIcon',
  'errorBoxText',
  'inputBg',
  'inputBorder',
  'inputFocusBorder',
  'inputPlaceholder',
  'cta',
  'ctaText',
  'cardChipBg',
  'cardChipText',
  'cardDivider',
  'warning',
  'footerLabel',
  'footerLink',
  // The CTA's "why is this disabled" tooltip. Its anchor sits inside the card and the
  // card clips it (`overflow: hidden`), so it is card material in the most literal sense.
  'tipBg',
  'tipText',
  'tipBorder',
  'tipShadow',
  // The /register password strength meter is painted on the kept-dark card too, so its track
  // and four graded fill colours must NOT derive a light variant (they were the last card
  // colours still doing so, which washed them out for light-theme users on the still-dark card).
  'strengthTrack',
  'strengthTooShort',
  'strengthWeak',
  'strengthFair',
  'strengthStrong',
];
for (const key of KEPT_DARK_LOGIN) {
  LIGHT_OVERRIDES[owsVar('LOGIN', key)] = LOGIN[key];
}

// Everything the REGISTER group owns (the marketing-consent checkbox) lives inside that
// same card, so the whole group is kept-dark with no per-key list to maintain.
for (const key of Object.keys(REGISTER) as (keyof typeof REGISTER)[]) {
  LIGHT_OVERRIDES[owsVar('REGISTER', key)] = REGISTER[key];
}

// The pencil on a hidden own list's disc (ListRow, Reorder lists) is ink on a fill that does not change: a `disc`
// reads as a fill, so the dark disc stays dark on light, and the pencil keeps its dark-theme lilac with it. Derived as
// text, it was deepened for the white card and fell to 2.79:1 on the disc. Read off the raw token, so it cannot drift.
LIGHT_OVERRIDES[owsVar('OWN_LIST', 'hiddenPencil')] = OWN_LIST.hiddenPencil;

export const lightValue = (name: string, dark: string): string => {
  const override = LIGHT_OVERRIDES[name];
  if (override) return override;
  if (isCompound(dark)) {
    return transformColorsIn(dark, (rgb) => deriveLightColor(rgb, name, LIGHT_PAGE));
  }
  const rgb = parseColor(dark);
  if (!rgb) return dark;
  return formatColor(deriveLightColor(rgb, name, LIGHT_CARD));
};
